/**
 * Authentication Store using Zustand
 * Manages auth state with persistence and synchronous access
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { isTokenExpired } from '../api/auth';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null;
  isVerified: boolean;
  avatar?: string;
  tagline?: string;
  profile_exists?: boolean;
  is_superuser?: boolean;
}

interface AuthState {
  // Core state
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Hydration tracking
  _hasHydrated: boolean;
  
  // Session tracking
  lastLoginDate: string | null;
  firstLoginDate: string | null;
  loginCount: number;
  
  // Actions
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasHydrated: (state: boolean) => void;
  updateSessionTracking: () => void;
  reset: () => void;
  
  // Getters for synchronous access (no race conditions!)
  getToken: () => string | null;
  getUser: () => User | null;
  isUserAuthenticated: () => boolean;
}

const initialState = {
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  _hasHydrated: false,
  lastLoginDate: null,
  firstLoginDate: null,
  loginCount: 0,
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,
        
        // Actions
        setToken: (token) => set((state) => {
          state.token = token;
          state.isAuthenticated = !!token;
          if (!token) {
            state.user = null;
          }
        }),
        
        setUser: (user) => set((state) => {
          state.user = user;
          if (user) {
            state.isAuthenticated = true;
          }
        }),
        
        setLoading: (loading) => set((state) => {
          state.isLoading = loading;
        }),
        
        setError: (error) => set((state) => {
          state.error = error;
        }),
        
        setHasHydrated: (hasHydrated) => set((state) => {
          state._hasHydrated = hasHydrated;
        }),
        
        updateSessionTracking: () => set((state) => {
          const now = new Date().toISOString();
          state.lastLoginDate = now;
          if (!state.firstLoginDate) {
            state.firstLoginDate = now;
          }
          state.loginCount += 1;
        }),
        
        // Reset auth state but KEEP hydration flag true so routes don't hang
        reset: () => set(() => ({
          ...initialState,
          _hasHydrated: true,
        })),
        
        // Synchronous getters - these prevent race conditions!
        getToken: () => get().token,
        getUser: () => get().user,
        isUserAuthenticated: () => get().isAuthenticated,
      })),
      {
        name: 'auth-storage',
        // Only persist essential data
        partialize: (state) => ({
          token: state.token,
          user: state.user,
          lastLoginDate: state.lastLoginDate,
          firstLoginDate: state.firstLoginDate,
          loginCount: state.loginCount,
        }),
        // Handle rehydration
        onRehydrateStorage: () => {
          console.log('Starting auth store rehydration...');
          return (state, error) => {
            if (error) {
              console.error('Auth rehydration error:', error);
            } else if (state) {
              // Validate token on rehydration
              // Clear expired tokens immediately to avoid UI showing protected routes
              if (state.token && isTokenExpired(state.token as unknown as string)) {
                state.token = null as any;
                state.user = null as any;
                state.isAuthenticated = false as any;
              } else {
                state.isAuthenticated = !!state.token;
              }
              state.setHasHydrated(true);
              console.log('Auth state rehydrated:', {
                hasToken: !!state.token,
                hasUser: !!state.user,
                loginCount: state.loginCount,
                hasHydrated: true
              });
            }
          };
        },
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Selector hooks for common patterns
export const useToken = () => useAuthStore((state) => state.token);
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// Helper to get state outside React components
export const getAuthState = () => useAuthStore.getState();
export const getStoredToken = () => useAuthStore.getState().getToken();
export const getStoredUser = () => useAuthStore.getState().getUser();
